import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAdminApi } from '@/hooks/useAdminApi';
import type { AuditLog, AuditLogEntityType, AuditLogValues } from '@ergshoot/shared';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Clock,
  Database,
  Eye,
  Filter,
  History,
  Loader2,
  RotateCcw,
  ShieldCheck,
  User,
} from 'lucide-react';
import { useMemo, useState } from 'react';

type AuditFilters = {
  action: string;
  actor_user_id: string;
  entity_type: '' | AuditLogEntityType;
  entity_id: string;
};

const ENTITY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All entities' },
  { value: 'user', label: 'Users' },
  { value: 'agent', label: 'Agents' },
  { value: 'affiliate', label: 'Affiliates' },
  { value: 'member', label: 'Members' },
  { value: 'event', label: 'Events' },
  { value: 'permission', label: 'Permissions' },
  { value: 'billing', label: 'Billing' },
  { value: 'track', label: 'Tracks' },
  { value: 'workout', label: 'Workouts' },
  { value: 'system', label: 'System' },
];

const EMPTY_FILTERS: AuditFilters = {
  action: '',
  actor_user_id: '',
  entity_type: '',
  entity_id: '',
};

function formatDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function labelize(value: string) {
  return value
    .replace(/[_\.]/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

function formatActor(log: AuditLog) {
  if (log.actor_email) return log.actor_email;
  if (log.actor_user_id) return log.actor_user_id;
  return 'System';
}

function valueKeys(values: AuditLogValues | null) {
  return values ? Object.keys(values) : [];
}

function changedKeys(log: AuditLog) {
  return Array.from(new Set([...valueKeys(log.previous_values), ...valueKeys(log.new_values)]));
}

function formatJson(values: AuditLogValues | null) {
  if (!values) return 'No values captured';
  return JSON.stringify(values, null, 2);
}

function roleBadgeVariant(role: string | null) {
  if (role === 'hq_admin') return 'admin';
  if (role === 'agent') return 'agent';
  if (role === 'affiliate_owner' || role === 'affiliate_coach') return 'affiliate';
  if (role === 'member') return 'member';
  return 'outline';
}

function entityBadgeClass(entityType: AuditLogEntityType) {
  switch (entityType) {
    case 'permission':
      return 'border-yellow-500/30 bg-yellow-500/10 text-yellow-300';
    case 'billing':
      return 'border-green-500/30 bg-green-500/10 text-green-300';
    case 'event':
      return 'border-blue-500/30 bg-blue-500/10 text-blue-300';
    case 'user':
    case 'member':
      return 'border-purple-500/30 bg-purple-500/10 text-purple-300';
    default:
      return 'border-border bg-muted/40 text-foreground';
  }
}

function AuditValueBlock({ title, values }: { title: string; values: AuditLogValues | null }) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
      <pre className="max-h-72 overflow-auto rounded-md border border-border bg-muted/40 p-3 text-xs leading-relaxed text-foreground">
        {formatJson(values)}
      </pre>
    </div>
  );
}

export default function AdminAuditLogs() {
  const adminApi = useAdminApi();
  const [draftFilters, setDraftFilters] = useState<AuditFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<AuditFilters>(EMPTY_FILTERS);
  const [cursor, setCursor] = useState<string | null>(null);
  const [previousCursors, setPreviousCursors] = useState<Array<string | null>>([]);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  const queryParams = useMemo(
    () => ({
      limit: 25,
      cursor: cursor || undefined,
      action: appliedFilters.action.trim() || undefined,
      actor_user_id: appliedFilters.actor_user_id.trim() || undefined,
      entity_type: appliedFilters.entity_type || undefined,
      entity_id: appliedFilters.entity_id.trim() || undefined,
    }),
    [appliedFilters, cursor]
  );

  const { data, error, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['admin-audit-logs', queryParams],
    queryFn: async () => {
      const response = await adminApi.getAuditLogs(queryParams);
      if (!response.success) throw new Error(response.error || 'Failed to load audit logs');
      return response.data;
    },
  });

  const logs = data?.logs ?? [];
  const filteredEntityLabel =
    ENTITY_OPTIONS.find((option) => option.value === appliedFilters.entity_type)?.label ||
    'All entities';

  const applyFilters = () => {
    setAppliedFilters({ ...draftFilters });
    setCursor(null);
    setPreviousCursors([]);
  };

  const clearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setCursor(null);
    setPreviousCursors([]);
  };

  const goNext = () => {
    if (!data?.next_cursor) return;
    setPreviousCursors((prev) => [...prev, cursor]);
    setCursor(data.next_cursor);
  };

  const goBack = () => {
    setPreviousCursors((prev) => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      setCursor(next.pop() ?? null);
      return next;
    });
  };

  return (
    <DashboardLayout>
      <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <History className="h-7 w-7 text-primary" />
            <h1 className="text-3xl font-bold text-gradient-gold">Audit Logs</h1>
          </div>
          <p className="mt-1 max-w-3xl text-muted-foreground">
            Trace administrative changes across users, affiliates, permissions, events, billing,
            programming, and system records.
          </p>
        </div>
        <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RotateCcw className="mr-2 h-4 w-4" />
          )}
          Refresh
        </Button>
      </div>

      <div className="mb-6 grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <Clock className="h-5 w-5 text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Visible entries</p>
              <p className="text-2xl font-bold">{logs.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <Database className="h-5 w-5 text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Entity filter</p>
              <p className="text-lg font-semibold">{filteredEntityLabel}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Access</p>
              <p className="text-lg font-semibold">HQ Admin only</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-primary" />
            Filters
          </CardTitle>
          <CardDescription>Use exact values for action, actor ID, or entity ID.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="audit-action">Action</Label>
              <Input
                id="audit-action"
                placeholder="permission.updated"
                value={draftFilters.action}
                onChange={(event) =>
                  setDraftFilters((current) => ({ ...current, action: event.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="audit-entity-type">Entity</Label>
              <Select
                id="audit-entity-type"
                options={ENTITY_OPTIONS}
                value={draftFilters.entity_type}
                onChange={(event) =>
                  setDraftFilters((current) => ({
                    ...current,
                    entity_type: event.target.value as AuditFilters['entity_type'],
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="audit-actor">Actor user ID</Label>
              <Input
                id="audit-actor"
                placeholder="user_..."
                value={draftFilters.actor_user_id}
                onChange={(event) =>
                  setDraftFilters((current) => ({
                    ...current,
                    actor_user_id: event.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="audit-entity-id">Entity ID</Label>
              <Input
                id="audit-entity-id"
                placeholder="member:log_scores"
                value={draftFilters.entity_id}
                onChange={(event) =>
                  setDraftFilters((current) => ({ ...current, entity_id: event.target.value }))
                }
              />
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={clearFilters}>
              Clear
            </Button>
            <Button onClick={applyFilters}>
              <Filter className="mr-2 h-4 w-4" />
              Apply Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Administrative Changes</CardTitle>
          <CardDescription>Newest changes appear first.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="flex min-h-64 flex-col items-center justify-center gap-3 text-center">
              <AlertCircle className="h-8 w-8 text-destructive" />
              <p className="text-sm text-muted-foreground">
                {error instanceof Error ? error.message : 'Failed to load audit logs'}
              </p>
              <Button variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex min-h-64 flex-col items-center justify-center gap-3 text-center">
              <History className="h-8 w-8 text-muted-foreground" />
              <p className="font-medium">No audit logs found</p>
              <p className="max-w-md text-sm text-muted-foreground">
                Try broadening the filters or perform an audited admin action.
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-40">Time</TableHead>
                    <TableHead className="min-w-56">Actor</TableHead>
                    <TableHead className="min-w-44">Action</TableHead>
                    <TableHead className="min-w-56">Entity</TableHead>
                    <TableHead className="min-w-48">Changed Values</TableHead>
                    <TableHead className="w-20 text-right">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => {
                    const keys = changedKeys(log);
                    return (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDateTime(log.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex min-w-0 flex-col gap-1">
                            <div className="flex min-w-0 items-center gap-2">
                              <User className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                              <span className="truncate font-medium">{formatActor(log)}</span>
                            </div>
                            {log.actor_role && (
                              <Badge variant={roleBadgeVariant(log.actor_role)} className="w-fit">
                                {labelize(log.actor_role)}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{labelize(log.action)}</div>
                          <div className="text-xs text-muted-foreground">{log.action}</div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge className={`w-fit ${entityBadgeClass(log.entity_type)}`}>
                              {labelize(log.entity_type)}
                            </Badge>
                            <span className="truncate text-sm">
                              {log.entity_label || log.entity_id}
                            </span>
                            {log.entity_label && (
                              <span className="truncate font-mono text-xs text-muted-foreground">
                                {log.entity_id}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {keys.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {keys.slice(0, 4).map((key) => (
                                <Badge key={key} variant="outline" className="font-mono">
                                  {key}
                                </Badge>
                              ))}
                              {keys.length > 4 && (
                                <Badge variant="secondary">+{keys.length - 4}</Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">Metadata only</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedLog(log)}
                            aria-label={`View audit log ${log.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  Page {previousCursors.length + 1}
                  {isFetching ? ' - updating...' : ''}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={goBack}
                    disabled={previousCursors.length === 0 || isFetching}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    onClick={goNext}
                    disabled={!data?.has_more || isFetching}
                  >
                    Next
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={selectedLog !== null} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          {selectedLog && (
            <>
              <DialogHeader>
                <DialogTitle>{labelize(selectedLog.action)}</DialogTitle>
                <DialogDescription>
                  {formatDateTime(selectedLog.created_at)} by {formatActor(selectedLog)}
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-md border border-border p-3">
                  <p className="text-xs text-muted-foreground">Entity</p>
                  <p className="mt-1 font-medium">
                    {selectedLog.entity_label || selectedLog.entity_id}
                  </p>
                  <p className="mt-1 font-mono text-xs text-muted-foreground">
                    {selectedLog.entity_type}:{selectedLog.entity_id}
                  </p>
                </div>
                <div className="rounded-md border border-border p-3">
                  <p className="text-xs text-muted-foreground">Request</p>
                  <p className="mt-1 truncate text-sm">
                    IP: {selectedLog.ip_address || 'Not captured'}
                  </p>
                  <p className="mt-1 truncate text-xs text-muted-foreground">
                    {selectedLog.user_agent || 'User agent not captured'}
                  </p>
                </div>
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                <AuditValueBlock title="Previous Values" values={selectedLog.previous_values} />
                <AuditValueBlock title="New Values" values={selectedLog.new_values} />
              </div>
              <AuditValueBlock title="Metadata" values={selectedLog.metadata} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
